import React, { Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';
import RouteKey from './key';
import RegisterExam from '../pages/examRegistration/RegisterExam';
import ReviewExam from '../pages/examRegistration/ReviewExam';
import Receipt from '../pages/examRegistration/Receipt';
import ReviewParticulars from '../pages/examRegistration/ReviewParticulars';
import Listing from '../pages/examRegistration/Listing';
import Summary from '../pages/examRegistration/Summary';
import SelectSubject from '../pages/examRegistration/SelectSubject';
import ViewSelectedSubjects from '../pages/examRegistration/ViewSelectedSubjects';
import CoordinatorRegisterExam from '../pages/groupExamRegistration/00_CoordinatorRegisterExam';
import PrivateRoute from '../modules/profile/components/PrivateRoute';
import { Role } from '../models';
import CoordinatorBulkUploadCandidates from '../pages/groupExamRegistration/01_CoordinatorBulkUploadCandidates';
import CoordinatorReviewRegistration from '../pages/groupExamRegistration/03_CoordinatorReviewRegistration';
import CoordinatorReviewCandidateDetails from '../pages/groupExamRegistration/02_CoordinatorReviewCandidateDetails';
import CoordinatorPaymentReceipt from '../pages/groupExamRegistration/04_CoordinatorPaymentReceipt';
import CoordinatorPaymentSummary from '../pages/groupExamRegistration/05_CoordinatorPaymentSummary';
import CoordinatorListing from '../pages/groupExamRegistration/CoordinatorListing';
import TransactionHistoryRegistrationPage from '../pages/subject/TransactionHistoryRegistrationPage';
import InvalidUniqueLink from '../pages/financial/InvalidUniqueLink';
import CoordinatorMakeEntryProofRequests from '../pages/entryProof/coordinator/MakeRequestListing';
import NonNationalExam from '../pages/examRegistration/NonNationalExam';
import UploadSupportingDocuments from '../pages/examRegistration/NonNationalExam/UploadSupportingDocuments';
import UploadAdditionalDocuments from '../pages/examRegistration/NonNationalExam/UploadAdditionalDocument';
import ConfirmParticulars from '../pages/examRegistration/NonNationalExam/ConfirmParticulars';
import AeisConfirmAndPay from '../pages/examRegistration/NonNationalExam/AEIS/AeisConfirmAndPay';
import RegistrationReceipt from '../pages/examRegistration/NonNationalExam/RegistrationReceipt';
import NonNationalCandidateDetail from '../pages/examRegistration/NonNationalExam/SPERS/NonNationalCandidateDetail';
import SpersExamSchoolingDetails from '../pages/examRegistration/NonNationalExam/SPERS/SpersExamSchoolingDetails';
import ExamSchoolingDetails from '../pages/examRegistration/NonNationalExam/AEIS/ExamSchoolingDetails';
import ParentParticulars from '../pages/examRegistration/NonNationalExam/AEIS/ParentParticulars';
import SpersConfirmAndPay from '../pages/examRegistration/NonNationalExam/SPERS/SpersConfirmAndPay';
import AeisRegistrationSummary from '../pages/examRegistration/NonNationalExam/AEIS/AeisRegistrationSummary';
import SpersRegistrationSummary from '../pages/examRegistration/NonNationalExam/SPERS/SpersRegistrationSummary';
import { IOptionType } from '../api/candidate/exam/model';
import ViewApplicationPage from '../pages/withdrawal/ViewApplicationPage';
import { HtmlTrans } from 'web-library';
import { NonNexPassportDisableContext } from './RootRouter';

const { error: errorRoute, exam } = RouteKey.child;
const {
  examRegistrations,
  registerExam,
  selectSubject,
  reviewExam,
  receipt,
  reviewParticulars,
  summary,
  viewSelectedSubjects,
  coordinatorRegisterExam,
  bulkUploadCandidates,
  coordinatorReviewExam,
  coordinatorReviewCandidates,
  coordinatorExamRegistrations,
  coordinatorEntryProofs,
  coordinatorViewReceipt,
  coordinatorViewSummary,
  transactionHistoryRegistration,
  invalid,
  nonNationalExaminations,
  parentParticulars,
  examSchoolingDetails,
  uploadSupportingDocuments,
  confirmParticulars,
  confirmAndPay,
  candidateDetails,
  uploadAdditionalResources,
} = RouteKey.exam.child;

const { transactionHistorySummary, nonNationalTransactionHistory } = RouteKey.withdrawal.child;

export const ExamNavigationContext = React.createContext<{
  toRegisterExamInstruction: string;
  toSelectSubject: string;
  toExamReview: string;
  toReviewParticulars: string;
}>({
  toRegisterExamInstruction: '',
  toSelectSubject: '',
  toExamReview: '',
  toReviewParticulars: '',
});

export const useExamNavigation = () => useContext(ExamNavigationContext);

interface IContextData {
  successStep: number;
  levelSeekingAdmission: IOptionType | undefined;
}

interface INonNationalExamNavigationContext {
  aeisData: IContextData;
  setAeisData: Dispatch<SetStateAction<IContextData>>;
  saeisData: IContextData;
  setSaeisData: Dispatch<SetStateAction<IContextData>>;
  spersData: IContextData;
  setSpersData: Dispatch<SetStateAction<IContextData>>;
  spersSecData: IContextData;
  setSpersSecData: Dispatch<SetStateAction<IContextData>>;
}

export const NonNationalExamNavigationContext = React.createContext<INonNationalExamNavigationContext>(
  {
    aeisData: { levelSeekingAdmission: undefined, successStep: 0 },
    saeisData: { levelSeekingAdmission: undefined, successStep: 0 },
    spersData: { levelSeekingAdmission: undefined, successStep: 0 },
    spersSecData: { levelSeekingAdmission: undefined, successStep: 0 },
    setAeisData: () => null,
    setSaeisData: () => null,
    setSpersData: () => null,
    setSpersSecData: () => null,
  }
);
export const useNonNationalNavigation = (examType: string) => {
  const {
    aeisData,
    saeisData,
    setAeisData,
    setSaeisData,
    setSpersData,
    setSpersSecData,
    spersData,
    spersSecData,
  } = useContext(NonNationalExamNavigationContext);

  switch (examType) {
    case EXAM_TYPE.SAEIS:
      return {
        contextData: saeisData,
        setContextData: setSaeisData,
      };
    case EXAM_TYPE.SPERS:
      return {
        contextData: spersData,
        setContextData: setSpersData,
      };
    case EXAM_TYPE.SPERSSec:
      return {
        contextData: spersSecData,
        setContextData: setSpersSecData,
      };

    default:
      return {
        contextData: aeisData,
        setContextData: setAeisData,
      };
  }
};

const getRouteSteps = (t: TFunction) => [
  {
    label: t('exam->>ExamRegistrationStepper.SelectSubject'),
  },
  {
    label: t('exam->>ExamRegistrationStepper.ViewSelectedSubject'),
  },
  {
    label: t('exam->>ExamRegistrationStepper.ConfirmParticular'),
  },
  {
    label: t('exam->>ExamRegistrationStepper.ConfirmAndPay'),
  },
];

const getCoordinatorRouteSteps = (t: TFunction) => [
  {
    label: t('exam->>ExamRegistrationStepper.UploadExcelTemplate'),
  },
  {
    label: t('exam->>ExamRegistrationStepper.ViewCandidateList'),
  },
  {
    label: t('exam->>ExamRegistrationStepper.ReviewRegistration'),
  },
];

const getNonNationalExamStep = (t: TFunction) => [
  {
    label: t('exam->>NonNationalExam.ApplicationDeclaration'),
  },
  {
    label: t('exam->>NonNationalExam.ExamDetail'),
  },
  {
    label: t('exam->>NonNationalExam.UploadDocument'),
  },
  {
    label: t('exam->>NonNationalExam.ConfirmParticulars'),
  },
  {
    label: t('exam->>NonNationalExam.ConfirmAndPay'),
  },
];

const getNonNationalExamPassportDisabledStep = (t: TFunction) => [
  {
    label: t('exam->>NonNationalExam.ApplicationDeclaration'),
  },
  {
    label: t('exam->>NonNationalExam.ExamDetail'),
  },
  {
    label: <HtmlTrans i18nKey="exam->>NonNationalExam.UploadDocumentDisabled" />,
    disabled: true,
  },
  {
    label: t('exam->>NonNationalExam.ConfirmParticulars'),
  },
  {
    label: t('exam->>NonNationalExam.ConfirmAndPay'),
  },
];

export interface IExamType {
  AEIS: string;
  SAEIS: string;
  SPERS: string;
  SPERSSec: string;
}

export const EXAM_TYPE: IExamType = {
  AEIS: 'AEIS',
  SAEIS: 'S_AEIS',
  SPERS: 'SPE',
  SPERSSec: 'SPERS_SEC',
};

const nonNationalExamTypes = [
  {
    path: 'aeis',
    examType: EXAM_TYPE.AEIS,
    examTitle: 'Admissions Exercise for International Students (AEIS)',
    isAeis: true,
  },
  {
    path: 's-aeis',
    examType: EXAM_TYPE.SAEIS,
    examTitle: 'Supplementary Admissions Exercise for International Students (S-AEIS)',
    isAeis: true,
  },

  {
    path: 'spe',
    examType: EXAM_TYPE.SPERS,
    examTitle: 'Supplementary Placement Exercise (SPE)',
    isAeis: false,
  },
  {
    path: 'spers-sec',
    examType: EXAM_TYPE.SPERSSec,
    examTitle: 'School Placement Exercise for Returning Singaporeans-Secondary (SPERS-Sec)',
    isAeis: false,
  },
];

export interface AEISRouteProps {
  nonNationalExamSteps: { label: string }[];
  examType: string;
}

const AEISRoute = ({ nonNationalExamSteps, examType }: AEISRouteProps) => (
  <>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${parentParticulars.route}`}
      roles={[Role.candidate]}
    >
      <ParentParticulars routeSteps={nonNationalExamSteps} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${examSchoolingDetails.route}`}
      roles={[Role.candidate]}
    >
      <ExamSchoolingDetails routeSteps={nonNationalExamSteps} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${uploadSupportingDocuments.route}`}
      roles={[Role.candidate]}
    >
      <UploadSupportingDocuments routeSteps={nonNationalExamSteps} isAEIS />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${confirmParticulars.route}`}
      roles={[Role.candidate]}
    >
      <ConfirmParticulars routeSteps={nonNationalExamSteps} isAEIS isOpsAdmin={false} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${confirmAndPay.route}`}
      roles={[Role.candidate]}
    >
      <AeisConfirmAndPay routeSteps={nonNationalExamSteps} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${receipt.route}`}
      roles={[Role.candidate]}
    >
      <RegistrationReceipt isAEIS />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${summary.route}`}
      roles={[Role.candidate]}
    >
      <AeisRegistrationSummary />
    </PrivateRoute>
  </>
);

interface SPERSRouteProps {
  nonNationalSpersSteps: { label: string | JSX.Element; disabled?: boolean }[];
  examType: string;
}

const SPERSRoute = ({ nonNationalSpersSteps, examType }: SPERSRouteProps) => (
  <>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${candidateDetails.route}`}
      roles={[Role.candidate]}
    >
      <NonNationalCandidateDetail routeSteps={nonNationalSpersSteps} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${examSchoolingDetails.route}`}
      roles={[Role.candidate]}
    >
      <SpersExamSchoolingDetails routeSteps={nonNationalSpersSteps} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${uploadSupportingDocuments.route}`}
      roles={[Role.candidate]}
    >
      <UploadSupportingDocuments routeSteps={nonNationalSpersSteps} isAEIS={false} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${confirmParticulars.route}`}
      roles={[Role.candidate]}
    >
      <ConfirmParticulars routeSteps={nonNationalSpersSteps} isAEIS={false} isOpsAdmin={false} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${confirmAndPay.route}`}
      roles={[Role.candidate]}
    >
      <SpersConfirmAndPay routeSteps={nonNationalSpersSteps} />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${receipt.route}`}
      roles={[Role.candidate]}
    >
      <RegistrationReceipt />
    </PrivateRoute>
    <PrivateRoute
      exact
      path={`/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${examType}/${summary.route}`}
      roles={[Role.candidate]}
    >
      <SpersRegistrationSummary />
    </PrivateRoute>
  </>
);

interface NonNationalExamRouteProps {
  path: string;
  nonNationalExamSteps: { label: string }[];
  nonNationalSpersSteps: { label: string }[];
  nonNationalExamPassportDisabledSteps: { label: string | JSX.Element; disabled?: boolean }[];
}

const NonNationalExamRoute = ({
  path,
  nonNationalExamSteps,
  nonNationalSpersSteps,
  nonNationalExamPassportDisabledSteps,
}: NonNationalExamRouteProps) => {
  const [aeisData, setAeisData] = useState<IContextData>({
    successStep: 0,
    levelSeekingAdmission: undefined,
  });
  const [saeisData, setSaeisData] = useState<IContextData>({
    successStep: 0,
    levelSeekingAdmission: undefined,
  });
  const [spersData, setSpersData] = useState<IContextData>({
    successStep: 0,
    levelSeekingAdmission: undefined,
  });
  const [spersSecData, setSpersSecData] = useState<IContextData>({
    successStep: 0,
    levelSeekingAdmission: undefined,
  });
  const nonNexPassportDisableContext = useContext(NonNexPassportDisableContext);

  const contextValue = useMemo(
    () => ({
      aeisData,
      saeisData,
      setAeisData,
      setSaeisData,
      setSpersData,
      setSpersSecData,
      spersData,
      spersSecData,
    }),
    [aeisData, saeisData, spersData, spersSecData]
  );

  return (
    <NonNationalExamNavigationContext.Provider value={contextValue}>
      {nonNationalExamTypes.map((type) => (
        <>
          <PrivateRoute
            exact
            key={type.path}
            path={`${path}/${examRegistrations.route}/${nonNationalExaminations.route}/${type.path}`}
            roles={[Role.candidate]}
          >
            <NonNationalExam examType={type.examType} examTitle={type.examTitle} path={type.path} />
          </PrivateRoute>
          <PrivateRoute
            exact
            path={`${path}/${examRegistrations.route}/${nonNationalExaminations.route}/:uuid/${type.path}/${uploadAdditionalResources.route}`}
            roles={[Role.candidate]}
          >
            <UploadAdditionalDocuments isAEIS={type.isAeis} />
          </PrivateRoute>
        </>
      ))}

      {/* AEIS */}
      <AEISRoute nonNationalExamSteps={nonNationalExamSteps} examType="aeis" />

      {/* S-AEIS */}
      <AEISRoute nonNationalExamSteps={nonNationalExamSteps} examType="s-aeis" />

      {/* SPERS */}
      <SPERSRoute
        nonNationalSpersSteps={
          nonNexPassportDisableContext.isPassportDisabled
            ? nonNationalExamPassportDisabledSteps
            : nonNationalSpersSteps
        }
        examType="spe"
      />

      {/* SPERS-Sec */}
      <SPERSRoute
        nonNationalSpersSteps={
          nonNexPassportDisableContext.isPassportDisabled
            ? nonNationalExamPassportDisabledSteps
            : nonNationalSpersSteps
        }
        examType="spers-sec"
      />
    </NonNationalExamNavigationContext.Provider>
  );
};

export const ExamRoute = () => {
  const { path } = useRouteMatch();
  const { t } = useTranslation();

  const registeredExamSummary = t('exam->>RegistrationSummary.ViewRegistration');
  const routeSteps = getRouteSteps(t);
  const nonNationalExamSteps = getNonNationalExamStep(t);
  const nonNationalExamPassportDisabledSteps = getNonNationalExamPassportDisabledStep(t);
  const coordinatorRouteSteps = getCoordinatorRouteSteps(t);

  const examNavigationContextValue = useMemo(
    () => ({
      toRegisterExamInstruction: `${path}/${examRegistrations.route}/${registerExam.route}/:examType`,
      toSelectSubject: `${path}/${examRegistrations.route}/:uuid/${selectSubject.route}`,
      toExamReview: `${path}/${examRegistrations.route}/:uuid/${reviewExam.route}`,
      toReviewParticulars: `${path}/${examRegistrations.route}/:uuid/${reviewParticulars.route}`,
    }),
    [path]
  );

  return (
    <ExamNavigationContext.Provider value={examNavigationContextValue}>
      <Switch>
        <Route exact path={`${path}/${examRegistrations.route}`}>
          <Listing />
        </Route>
        <PrivateRoute
          exact
          path={`${path}/${examRegistrations.route}/${registerExam.route}/:examType`}
          roles={[Role.candidate]}
        >
          <RegisterExam />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${examRegistrations.route}/:uuid/${selectSubject.route}`}
          roles={[Role.candidate]}
        >
          <SelectSubject routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${examRegistrations.route}/:uuid/${viewSelectedSubjects.route}`}
          roles={[Role.candidate]}
        >
          <ViewSelectedSubjects routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${examRegistrations.route}/:uuid/${reviewParticulars.route}`}
          roles={[Role.candidate]}
        >
          <ReviewParticulars routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${examRegistrations.route}/:uuid/${reviewExam.route}`}
          roles={[Role.candidate]}
        >
          <ReviewExam routeSteps={routeSteps} />
        </PrivateRoute>

        <Route exact path={`${path}/${examRegistrations.route}/:uuid/${receipt.route}`}>
          <Receipt />
        </Route>

        <Route exact path={`${path}/${examRegistrations.route}/:uuid/${summary.route}`}>
          <Summary title={registeredExamSummary} />
        </Route>

        <Route
          exact
          path={`${path}/${examRegistrations.route}/:uuid/${transactionHistoryRegistration.route}`}
        >
          <TransactionHistoryRegistrationPage />
        </Route>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${transactionHistorySummary.route}`}
          roles={[Role.candidate]}
        >
          <ViewApplicationPage isTransactionHistory />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/aeis/${nonNationalTransactionHistory.route}`}
          roles={[Role.candidate]}
        >
          <AeisRegistrationSummary isTransactionHistory />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/spers/${nonNationalTransactionHistory.route}`}
          roles={[Role.candidate]}
        >
          <SpersRegistrationSummary isTransactionHistory />
        </PrivateRoute>

        {/** Coordinator Exam Registration */}
        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorListing />
        </PrivateRoute>
        <PrivateRoute
          exact
          path={`${path}/${coordinatorExamRegistrations.route}/${coordinatorRegisterExam.route}/:examType`}
          roles={[Role.coordinator]}
        >
          <CoordinatorRegisterExam />
        </PrivateRoute>
        <PrivateRoute
          path={`${path}/${coordinatorExamRegistrations.route}/:uuid/${bulkUploadCandidates.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorBulkUploadCandidates routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>
        <PrivateRoute
          path={`${path}/${coordinatorExamRegistrations.route}/:uuid/${coordinatorReviewCandidates.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorReviewCandidateDetails routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>
        <PrivateRoute
          path={`${path}/${coordinatorExamRegistrations.route}/:uuid/${coordinatorReviewExam.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorReviewRegistration routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>
        <PrivateRoute
          path={`${path}/${coordinatorExamRegistrations.route}/:uuid/${coordinatorViewReceipt.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorPaymentReceipt />
        </PrivateRoute>
        <PrivateRoute
          path={`${path}/${coordinatorExamRegistrations.route}/:uuid/${coordinatorViewSummary.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorPaymentSummary />
        </PrivateRoute>
        <PrivateRoute
          path={`${path}/${coordinatorExamRegistrations.route}/:uuid/${invalid.route}`}
          roles={[Role.coordinator]}
        >
          <InvalidUniqueLink />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${coordinatorEntryProofs.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorMakeEntryProofRequests />
        </PrivateRoute>

        {/* Start For Non National Examinations */}
        <NonNationalExamRoute
          nonNationalExamSteps={nonNationalExamSteps}
          nonNationalSpersSteps={nonNationalExamSteps}
          nonNationalExamPassportDisabledSteps={nonNationalExamPassportDisabledSteps}
          path={path}
        />
        {/* End For Non National Examinations */}

        <Route path="*" exact>
          <Redirect to={`/${errorRoute}`} />
        </Route>
      </Switch>
    </ExamNavigationContext.Provider>
  );
};

export default ExamRoute;
